import React, { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import * as Sentry from '@sentry/browser'
import * as Types from 'types'
import {
  ComponentFailure,
  FullPageSpinner,
  NoTableResults,
  QuickFilterCard,
  Spinner,
} from 'components'
import {
  TaxableAccountsAgreementsTable,
  UploadPlanAgreementModal,
} from '../components'
import {
  useServicesAgreementsFilters,
  INITIAL_FILTERS,
} from '../hooks/useServicesAgreementsFilters'
import * as apiActions from 'api-actions'
import {
  Path,
  TaxableAccountsActionItems,
  FLASH_SUCCESS_WITH_USER_ACTION_TIMEOUT,
} from 'config/portal'
import { ProductTypeTitles, ProductAgreementType } from 'config/products'
import { ServicesAgreementStatus } from 'config/automatic-rollovers'
import * as actions from '../actions'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { selectors as globalSelectors } from 'global-reducer'
import { selectors } from '../reducer'
import { isEqual, some } from 'lodash'
import { useLocation, useHistory } from 'react-router-dom'
import { getParticipantFileSubmitSuccessMessage, isDateInRange } from 'utils'
import { subDays, startOfToday } from 'date-fns'
import { flashSuccessMessage } from 'redux-flash'
import { Icon as PlusIcon } from 'images/plus.svg'
import { Icon as CheckmarkIcon } from 'images/status-icons/checkmark.svg'
import { Icon as AlertIcon } from 'images/status-icons/alert.svg'

const TABLE_ID = 'taxable-accounts-services-agreements-table'

const today = startOfToday()
const startOfRecentDate = subDays(today, 7) // recent being within the last 7 days

/* --- Quick filters criteria --- */
// Recently Approved Agreements are Services Agreements with
// a portalAgreementStatus of Completed and a createdDate within the last 7 days
const RECENTLY_APPROVED_FILTERS = {
  ...INITIAL_FILTERS,
  agreementStatus: [ServicesAgreementStatus.COMPLETED],
  startLastUpdatedDate: startOfRecentDate,
  endLastUpdatedDate: today,
}

// Missing Signatures are Services Agreements with a portalAgreementStatus of Pending signatures
const MISSING_SIGNATURES_FILTERS = {
  ...INITIAL_FILTERS,
  agreementStatus: [ServicesAgreementStatus.PENDING_SIGNATURES],
}

function TableComponentFailure() {
  return (
    <div className="content-wrapper all-plans">
      <ServicesAgreementsHeader />
      <div className="services-agreements-table-wrapper">
        <ComponentFailure />
      </div>
    </div>
  )
}

const propTypes = {
  servicesAgreementsTaxableAccounts: PropTypes.arrayOf(
    Types.servicesAgreementSummary
  ),
  fetchServicesAgreementsTaxableAccounts: PropTypes.func.isRequired,
  shouldTriggerAllTaxableServicesAgreementsFetch: PropTypes.bool.isRequired,
  setShouldTriggerAllTaxableServicesAgreementsFetch: PropTypes.func.isRequired,
  currentUser: Types.user.isRequired,
  flashSuccessMessageHandler: PropTypes.func.isRequired,
}

const defaultProps = {
  servicesAgreementsTaxableAccounts: [],
}

ServicesAgreementsHeader.propTypes = {
  children: PropTypes.node,
}

function ServicesAgreementsHeader({ children }) {
  return (
    <div className="header-bar">
      <div className="header-bar-content">
        <div className="header-bar-page-title">
          <h1>{ProductTypeTitles.TAXABLE_ACCOUNTS}</h1>
        </div>
        {children}
      </div>
    </div>
  )
}

function TaxableAccountsServiceAgreements({
  servicesAgreementsTaxableAccounts,
  fetchServicesAgreementsTaxableAccounts,
  shouldTriggerAllTaxableServicesAgreementsFetch,
  setShouldTriggerAllTaxableServicesAgreementsFetch,
  currentUser,
  flashSuccessMessageHandler,
}) {
  const location = useLocation()
  const history = useHistory()
  const noServicesAgreements = !servicesAgreementsTaxableAccounts?.length

  useEffect(() => {
    globalThis.appEventData.pop()
    let appEventData = globalThis.appEventData || []
    appEventData.push({
      event: 'Page Load Completed',
    })
  }, [])

  const [error, setError] = useState(false)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [selectedServicesAgreement, setSelectedServicesAgreement] =
    useState(null)
  const [showTaxableAccountsSuccessModal, setShowTaxableAccountsSuccessModal] =
    useState(!!location.state?.showTaxableAccountsSuccessModal)

  // Determines if the Taxable Accounts submission--which the user was redirected from--
  // was partial (i.e., at least one plan was missing an agreement) or complete (i.e., every plan was mapped to an agreement)
  // This information is only relevant when the component first mounts and the success modal is shown
  const isTaxableAccountsPartialSubmission = useMemo(
    () => !!location.state?.isPartialSubmission,
    []
  )

  useEffect(() => {
    // Reset location.state from flow so the modal doesn't remain open when the page is refreshed
    history.replace(location.pathname + location.search + location.hash)

    async function getServicesAgreements() {
      try {
        await fetchServicesAgreementsTaxableAccounts()
        setShouldTriggerAllTaxableServicesAgreementsFetch(false)
      } catch (error) {
        Sentry.captureException(error)
        setError(true)
      }
    }
    if (shouldTriggerAllTaxableServicesAgreementsFetch) {
      getServicesAgreements()
    }
  }, [shouldTriggerAllTaxableServicesAgreementsFetch])

  useEffect(() => {
    if (showTaxableAccountsSuccessModal) {
      const successMessage = getParticipantFileSubmitSuccessMessage(
        isTaxableAccountsPartialSubmission
      )
      flashSuccessMessageHandler(successMessage, {
        timeout: FLASH_SUCCESS_WITH_USER_ACTION_TIMEOUT,
      })
      setShowTaxableAccountsSuccessModal(false)
    }
  }, [
    showTaxableAccountsSuccessModal,
    flashSuccessMessageHandler,
    isTaxableAccountsPartialSubmission,
    setShowTaxableAccountsSuccessModal,
  ])

  const {
    filters,
    searchValue,
    updateSearchParams,
    filteredServicesAgreements,
  } = useServicesAgreementsFilters({
    servicesAgreements: servicesAgreementsTaxableAccounts,
    currentUser,
  })

  const isLoading = !servicesAgreementsTaxableAccounts && !error

  if (isLoading) return <FullPageSpinner />
  if (error) return <TableComponentFailure />

  const hasCompletedServicesAgreement = some(
    servicesAgreementsTaxableAccounts,
    (agreement) =>
      agreement.portalAgreementStatus === ServicesAgreementStatus.COMPLETED
  )

  const handleAddPlanAgreementAction = () =>
    history.push({
      pathname: Path.ADD_NEW_TAXABLE_SERVICES_AGREEMENT,
      state: {
        search: history.location.search,
      },
    })

  const taxableAccountsAddendumActions = () => {
    return (
      <>
        <button
          type="button"
          className="button-text"
          onClick={() => handleAddPlanAgreementAction()}
        >
          <PlusIcon aria-hidden="true" />
          {TaxableAccountsActionItems.CREATE_NEW_SERVICES_AGREEMENT}
        </button>

        {hasCompletedServicesAgreement && (
          <button
            type="button"
            onClick={() => handleInitiateParticipantUploadAction()}
            className="button-primary"
          >
            {TaxableAccountsActionItems.OPEN_TAXABLE_ACCOUNT}
          </button>
        )}
      </>
    )
  }

  const productActionItems = () => {
    return taxableAccountsAddendumActions()
  }

  /**
   * @description Handles onClick action for when the user clicks on ***Open Taxable Account*** button
   */
  const handleInitiateParticipantUploadAction = () =>
    history.push({
      pathname: '/taxable-accounts/upload-file',
      state: {
        hasConfirmed: true,
        search: location.search,
      },
    })

  // Quick filters counter
  const missingSignaturesCount = servicesAgreementsTaxableAccounts.reduce(
    (acc, { portalAgreementStatus }) => {
      if (portalAgreementStatus === ServicesAgreementStatus.PENDING_SIGNATURES)
        acc++
      return acc
    },
    0
  )

  const recentlyApprovedAgreementsCount =
    servicesAgreementsTaxableAccounts.reduce(
      (acc, { portalAgreementStatus, createdDate }) => {
        const isRecent = isDateInRange({
          date: createdDate,
          startOfDateRange: startOfRecentDate,
          endOfDateRange: today,
        })
        if (
          portalAgreementStatus === ServicesAgreementStatus.COMPLETED &&
          isRecent
        )
          acc++
        return acc
      },
      0
    )

  const fieldMappings = [
    { field: 'planSponsorCompany.name', displayName: 'Plan Sponsor Name' },
    { field: 'planEIN', displayName: 'EIN' },
    { field: 'name', displayName: 'Complete Legal Plan Name' },
    { field: 'portalAgreementType', displayName: 'Initial Investment' },
    { field: 'portalAgreementStatus', displayName: 'Agreement Status' },
    { field: 'createdDate', displayName: 'Date created' },
  ]

  return (
    <div className="content-wrapper all-plans">
      <ServicesAgreementsHeader>
        <div className="services-agreements-actions">
          {productActionItems()}
        </div>
      </ServicesAgreementsHeader>
      <div className="quick-filters-container">
        <div className="flex-columns">
          <div className="flex-column">
            <QuickFilterCard
              controlId={TABLE_ID}
              isActive={isEqual(filters, RECENTLY_APPROVED_FILTERS)}
              count={recentlyApprovedAgreementsCount}
              label="Recently approved agreements"
              icon={CheckmarkIcon}
              onClick={() => {
                if (isEqual(filters, RECENTLY_APPROVED_FILTERS))
                  updateSearchParams(INITIAL_FILTERS, searchValue)
                else updateSearchParams(RECENTLY_APPROVED_FILTERS, searchValue)
              }}
            />
          </div>
          <div className="flex-column">
            <QuickFilterCard
              controlId={TABLE_ID}
              isActive={isEqual(filters, MISSING_SIGNATURES_FILTERS)}
              count={missingSignaturesCount}
              label="Missing signatures"
              icon={AlertIcon}
              onClick={() => {
                if (isEqual(filters, MISSING_SIGNATURES_FILTERS))
                  updateSearchParams(INITIAL_FILTERS, searchValue)
                else updateSearchParams(MISSING_SIGNATURES_FILTERS, searchValue)
              }}
            />
          </div>
        </div>
      </div>
      {shouldTriggerAllTaxableServicesAgreementsFetch && <Spinner />}
      <TaxableAccountsAgreementsTable
        servicesAgreements={filteredServicesAgreements || []}
        handleUploadClick={(servicesAgreement) => {
          setSelectedServicesAgreement(servicesAgreement)
          setShowUploadModal(true)
        }}
        tableId={TABLE_ID}
        currentUser={currentUser}
        filters={filters}
        fieldMappings={fieldMappings}
        noServicesAgreements={noServicesAgreements}
        searchValue={searchValue}
        noRowsFoundMessage={
          <NoTableResults
            title={
              noServicesAgreements
                ? 'You don’t have any service agreements at this time.'
                : 'No results found.'
            }
            titleAs="h2"
            details={
              noServicesAgreements
                ? 'Need to create a new agreement? You can do that now.'
                : 'Try adjusting your filter or search.'
            }
          >
            {noServicesAgreements && (
              <button
                type="button"
                className="button-primary"
                onClick={handleAddPlanAgreementAction}
              >
                {TaxableAccountsActionItems.CREATE_NEW_SERVICES_AGREEMENT}
              </button>
            )}
          </NoTableResults>
        }
      />
      {showUploadModal && (
        <UploadPlanAgreementModal
          onClose={() => {
            setShowUploadModal(false)
            setSelectedServicesAgreement(null)
          }}
          servicesAgreement={selectedServicesAgreement}
          onSubmitSuccess={() => {
            setShowUploadModal(false)
            setSelectedServicesAgreement(null)
            setShouldTriggerAllTaxableServicesAgreementsFetch(true)
          }}
          agreementsType={ProductAgreementType.TAXABLE_ACCOUNTS}
        />
      )}
    </div>
  )
}

function mapStateToProps(state) {
  return {
    servicesAgreementsTaxableAccounts:
      selectors.servicesAgreementsTaxableAccounts(state),
    shouldTriggerAllTaxableServicesAgreementsFetch:
      selectors.shouldTriggerAllTaxableServicesAgreementsFetch(state),
    currentUser: globalSelectors.currentUser(state),
  }
}

const mapDispatchToProps = {
  fetchServicesAgreementsTaxableAccounts:
    apiActions.fetchServicesAgreementsTaxableAccounts,
  setShouldTriggerAllTaxableServicesAgreementsFetch:
    actions.setShouldTriggerAllTaxableServicesAgreementsFetch,
  flashSuccessMessageHandler: flashSuccessMessage,
}
TaxableAccountsServiceAgreements.propTypes = propTypes
TaxableAccountsServiceAgreements.defaultProps = defaultProps

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  TaxableAccountsServiceAgreements
)
